import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Grid, Modal, Segmented, Space, Typography } from 'antd';

import ConditionBuilder from '../common/ConditionBuilder';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import ToggleRow from '../common/ToggleRow';
import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import { InputSelect } from '../Input';
import { buildCardHierarchyOptions } from '../cards/CardVisuals';
import { PLUS } from '../icons';
import TagBadge from './TagBadge';
import TagRuleClause from './TagRuleClause';
import TagRulePass from './TagRulePass';
import { confirmTagRuleStrict } from './confirmTagRule';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getTagRule, previewTagRule, putTagRule, setTagRuleEnabled } from '../../services/tags';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { tagIdOf, tagOwnerCollectionId, tagParentIdOf } from '../../utils/tags';
import {
	DATE_OPS,
	RULE_LIMITS,
	STRICT_WORDS,
	cardSingular,
	countWords,
	draftFromRule,
	draftGap,
	emptyClause,
	partCount,
	previewWords,
	relatedCardsFor,
	ruleErrorTarget,
	ruleFromDraft,
	sameRule,
	tagRuleRefusal,
	timeDependentNote,
} from '../../utils/tagRules';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

const { Text } = Typography;

/**
 * "APPLIED BY A RULE" — an automatic tag's rule, authored on its tag
 * (docs/plans/tags.md §3.4, §5.2 Settings › Tags; keepr-api docs/SCHEMA.md
 * § Automatic tags). Managers of the tag's collection only: Settings › Tags
 * is theirs, and the rule's routes answer 403 to anyone else.
 *
 * THE RULE, top to bottom — each part appears once what it depends on is
 * answered, and nothing answered is hidden behind a step:
 *
 *   Tag items of this card   the rule's card (descendant cards too)
 *   Only items matching      a filter on the item itself — the shared
 *                            ConditionBuilder, collection mode, names
 *                            written as ids; the tag itself and the tags
 *                            above it are greyed (a rule that reads itself
 *                            latches or flips — the API refuses it)
 *   Related items 1–3        aggregates over the items that point at this
 *                            one (components/tags/TagRuleClause)
 *   Tag an item when         all of these hold · any one holds (with 2+ parts)
 *   Who applies it           "People can apply it too" (the default, ruled
 *                            d11b) · "Only this rule applies this tag"
 *   Preview                  how many items it would tag, and a few of them
 *
 * SAVING (the house rule for a write that takes things away): a DRY RUN
 * first (`PUT …/rule { dryRun }`). Turning strict on while people applied the
 * tag by hand answers `removesHand` and a digest; that count is confirmed
 * (confirmTagRuleStrict) and the real call echoes the digest as `confirm`. A
 * count that moved meanwhile is 409 `confirm_required`: the dry run is asked
 * again and the new count confirmed. The saved rule's pass — the work of
 * making it true — starts on the server; the editor closes (a save finishes
 * the modal, platform-standards §8) and the tag's row follows the pass.
 *
 * PAUSE AND RESUME are the rule's enable switch (ruled d20), applied at once
 * like every automation's switch: pausing takes the rule's own applications
 * off, resuming puts back what holds. **Stop applying by rule…** (the Danger
 * group's act, `onStop`) turns the automatic applications into hand ones.
 *
 * A TIME-DEPENDENT rule (a date comparison, a window, a relative date) says
 * plainly when keepr checks items again: whenever they change, and once a
 * day — just after midnight in the collection's time zone, named with where
 * it comes from (the rule's `daily`, keepr-api T6b and the tags rulings'
 * b65) — and the next check as a date and time on the person's own clock
 * (utils/tagRules.js timeDependentNote). Before a save there is no time
 * yet; paused, nothing.
 *
 * The PREVIEW's examples name their own collection and card (T6b): each
 * badge wears its item's card tile and links into its own collection.
 *
 * Props: `tag` (the collection tag), `collection` (for its cards; a caller
 * may hand `collectionCards` / `cardsById` / `getElementsForCard` itself),
 * `onSaved(result)` (`{ payload, rule, enabled, timeDependent, daily, pass }`),
 * `onPass(tagId, pass)`, `onEnabled(tagId, enabled)` (the tree's Paused),
 * `onStop(tag, { report })` → Promise<boolean>.
 */
export default function TagRuleEditor({
	open,
	tag,
	collection = null,
	onClose,
	onSaved,
	onPass,
	onEnabled,
	onStop,
	collectionCards: cardsProp,
	cardsById: cardsByIdProp,
	getElementsForCard: elementsProp,
}) {
	const { message, modal } = App.useApp();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const tagId = tagIdOf(tag);
	const collectionId = tagOwnerCollectionId(tag);
	const tagName = tag?.name || 'this tag';

	// ---- the collection's cards ------------------------------------------
	const cardDefinitions = useOptionalCardDefinitions();
	const cardsById = useMemo(() => cardsByIdProp || cardDefinitions?.cardsById || {}, [cardsByIdProp, cardDefinitions]);
	const collectionCards = useMemo(() => {
		if (Array.isArray(cardsProp)) return cardsProp;
		const all = Object.values(cardsById);
		const members = getCollectionMemberCardIds(collection, all);
		return all.filter(card => members.has(normalizeId(card?._id)));
	}, [cardsProp, cardsById, collection]);
	const getElements = elementsProp || cardDefinitions?.getElementsForCard;
	const elementsFor = useCallback(cardId => {
		if (!cardId) return [];
		if (typeof getElements === 'function') return getElements(cardId) || [];
		return cardsById[cardId]?.elements || [];
	}, [getElements, cardsById]);
	const cardOptions = useMemo(() => buildCardHierarchyOptions(collectionCards), [collectionCards]);

	// ---- the rule as the server holds it ---------------------------------
	const [load, setLoad] = useState({ loading: true, error: null, stored: null });
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [draft, setDraft] = useState(() => draftFromRule(null));
	const [preview, setPreview] = useState({ data: null, stale: false, loading: false });
	const [refusal, setRefusal] = useState(null); // { text, target }
	const [saving, setSaving] = useState(false);
	const [switching, setSwitching] = useState(false);
	const [stopping, setStopping] = useState(false);

	useEffect(() => {
		if (!open || !tagId) return undefined;
		let active = true;
		setLoad({ loading: true, error: null, stored: null });
		setDraft(draftFromRule(null));
		setPreview({ data: null, stale: false, loading: false });
		setRefusal(null);
		getTagRule(tagId)
			.then(stored => {
				if (!active) return;
				setLoad({ loading: false, error: null, stored });
				setDraft(draftFromRule(stored.rule));
			})
			.catch(error => { if (active) setLoad({ loading: false, error, stored: null }); });
		return () => { active = false; };
	}, [open, tagId, loadAttempt]);

	const stored = load.stored;
	const hasRule = Boolean(stored?.rule);
	const enabled = hasRule ? stored.enabled !== false : true;

	// ---- the rule's own tag, and the tags above it -----------------------
	const { tags: vocabulary } = useCollectionTags(collectionId, { enabled: Boolean(open && collectionId) });
	const ownIds = useMemo(() => {
		const ids = new Set(tagId ? [tagId] : []);
		const byId = new Map((vocabulary || []).map(t => [tagIdOf(t), t]));
		let cursor = tagParentIdOf(tag);
		while (cursor && !ids.has(cursor)) {
			ids.add(cursor);
			cursor = tagParentIdOf(byId.get(cursor));
		}
		return ids;
	}, [vocabulary, tag, tagId]);
	const tagDisabledFor = useCallback(
		candidate => (ownIds.has(tagIdOf(candidate)) ? 'A rule can’t depend on its own tag, or a tag above it.' : null),
		[ownIds]
	);

	// ---- editing ---------------------------------------------------------
	const touched = useCallback(() => {
		setPreview(prev => (prev.data ? { ...prev, stale: true } : prev));
		setRefusal(null);
	}, []);
	const patch = useCallback(fields => {
		setDraft(prev => ({ ...prev, ...fields }));
		touched();
	}, [touched]);

	// One stable handler per clause key, so a clause's own effects (the
	// lookup chosen for you, a comparison replaced) never loop on a new
	// function every render.
	const clauseHandlers = useRef(new Map());
	const handlerFor = key => {
		if (!clauseHandlers.current.has(key)) {
			clauseHandlers.current.set(key, fields => {
				setDraft(prev => ({ ...prev, related: prev.related.map(clause => (clause.key === key ? { ...clause, ...fields } : clause)) }));
				touched();
			});
		}
		return clauseHandlers.current.get(key);
	};
	const addClause = () => {
		setDraft(prev => (prev.related.length >= RULE_LIMITS.related ? prev : { ...prev, related: [...prev.related, emptyClause()] }));
		touched();
	};
	const removeClause = key => {
		setDraft(prev => ({ ...prev, related: prev.related.filter(clause => clause.key !== key) }));
		clauseHandlers.current.delete(key);
		touched();
	};

	const setCard = cardId => {
		// A lookup that pointed at the old card may not point at the new one:
		// those clauses keep everything but the lookup, chosen again.
		setDraft(prev => ({ ...prev, card_id: cardId, related: prev.related.map(clause => ({ ...clause, link: '' })) }));
		touched();
	};

	const relatedCards = useMemo(
		() => relatedCardsFor(draft.card_id, collectionCards, elementsFor, cardsById),
		[draft.card_id, collectionCards, elementsFor, cardsById]
	);
	const ruleCard = cardsById[draft.card_id] || collectionCards.find(card => normalizeId(card?._id) === draft.card_id) || null;
	const gap = draftGap(draft, { elementsFor });

	const placed = section => (refusal?.target && refusal.target.section === section ? refusal.text : null);
	const clauseError = index => {
		if (!refusal?.target || refusal.target.section !== 'related' || refusal.target.index !== index) return null;
		return { key: refusal.target.key || 'card_id', text: refusal.text };
	};
	// An element the rule's filter could mean several of (400
	// `ambiguous_element`, element ids step 3): the person picks one, or any
	// of these cards, and the preview or the save goes again with the pick.
	// …answering exactly the rule it was made for: any change forgets it.
	const elementPicks = useElementChoices(JSON.stringify(ruleFromDraft(draft)));
	// Which of the two asked last: a pick answers that one again.
	const lastAskRef = useRef('save');
	const refuse = (error, fallback) => {
		if (elementPicks.take(error)) { setRefusal(null); return; }
		setRefusal({ text: tagRuleRefusal(error, fallback ? { fallback } : undefined), target: ruleErrorTarget(error) });
	};

	// ---- preview ---------------------------------------------------------
	const runPreview = async () => {
		if (gap) { setRefusal({ text: gap, target: { section: null } }); return; }
		lastAskRef.current = 'preview';
		setPreview(prev => ({ ...prev, loading: true }));
		try {
			const data = elementPicks.choices()
				? await previewTagRule(tagId, ruleFromDraft(draft), elementPicks.withChoices({}))
				: await previewTagRule(tagId, ruleFromDraft(draft));
			setPreview({ data, stale: false, loading: false });
		} catch (error) {
			setPreview({ data: null, stale: false, loading: false });
			refuse(error, 'The preview could not be worked out. Please try again.');
		}
	};

	// ---- save ------------------------------------------------------------
	const commit = async (rule, confirm, retried = false) => {
		try {
			return await putTagRule(tagId, rule, elementPicks.withChoices({ confirm }));
		} catch (error) {
			const data = error?.response?.data || {};
			if (!retried && error?.response?.status === 409 && data.code === 'confirm_required') {
				// The count moved since the dry run: ask again, confirm again.
				const again = await putTagRule(tagId, rule, elementPicks.withChoices({ dryRun: true }));
				if (again?.removesHand > 0) {
					const ok = await confirmTagRuleStrict(modal, { tagName, count: again.removesHand, changed: true });
					if (!ok) return null;
					return commit(rule, again.confirm, true);
				}
				return commit(rule, undefined, true);
			}
			throw error;
		}
	};

	const save = async () => {
		if (gap) { setRefusal({ text: gap, target: { section: null } }); return; }
		// The same rule sent again changes nothing — except a strict one, whose
		// hand applications may still be there with no pass taking them off:
		// the API restarts that removal (its heal), so a strict rule is always
		// asked.
		if (hasRule && sameRule(stored.rule, draft) && !draft.strict) {
			message.info('Nothing changed.');
			onClose?.();
			return;
		}
		const rule = ruleFromDraft(draft);
		lastAskRef.current = 'save';
		setSaving(true);
		setRefusal(null);
		try {
			const dry = await putTagRule(tagId, rule, elementPicks.withChoices({ dryRun: true }));
			if (dry?.preview) setPreview({ data: { ...dry.preview, timeDependent: dry.timeDependent }, stale: false, loading: false });
			if (dry?.change === 'none') {
				const again = draft.strict ? await (elementPicks.choices() ? putTagRule(tagId, rule, elementPicks.withChoices({})) : putTagRule(tagId, rule)) : null;
				if (again?.pass) {
					message.success(`Taking “${tagName}” off the items people tagged by hand that were left.`);
					onSaved?.(again);
				} else {
					message.info('Nothing changed.');
				}
				onClose?.();
				return;
			}
			let confirm;
			if (dry?.removesHand > 0) {
				const ok = await confirmTagRuleStrict(modal, { tagName, count: dry.removesHand });
				if (!ok) return;
				confirm = dry.confirm;
			}
			const result = await commit(rule, confirm);
			if (!result) return;
			message.success(hasRule ? `Saved the rule for “${tagName}”.` : `“${tagName}” is now applied by a rule.`);
			onSaved?.(result);
			onClose?.();
		} catch (error) {
			refuse(error);
		} finally {
			setSaving(false);
		}
	};

	// ---- pause and resume ------------------------------------------------
	const toggleEnabled = async next => {
		const automationId = normalizeId(tag?.rule?.automation_id);
		if (!automationId || !stored) return;
		const before = stored;
		setSwitching(true);
		setRefusal(null);
		setLoad(prev => ({ ...prev, stored: { ...prev.stored, enabled: next } }));
		try {
			const answer = await setTagRuleEnabled(collectionId, automationId, next);
			setLoad(prev => ({ ...prev, stored: { ...prev.stored, enabled: answer.enabled, pass: answer.pass || prev.stored.pass } }));
			onEnabled?.(tagId, answer.enabled);
			if (answer.pass) onPass?.(tagId, answer.pass);
			message.success(next ? `Resumed the rule for “${tagName}”.` : `Paused the rule for “${tagName}”.`);
		} catch (error) {
			setLoad(prev => ({ ...prev, stored: before }));
			setRefusal({ text: tagRuleRefusal(error, { fallback: 'The rule could not be switched. Please try again.' }), target: { section: null } });
		} finally {
			setSwitching(false);
		}
	};

	// ---- stop ------------------------------------------------------------
	const stop = async () => {
		if (typeof onStop !== 'function') return;
		setStopping(true);
		try {
			const stopped = await onStop(tag, { report: text => setRefusal({ text, target: { section: null } }) });
			if (stopped) onClose?.();
		} finally {
			setStopping(false);
		}
	};

	// ---- what the page says ----------------------------------------------
	const timeDependent = Boolean(
		(preview.data && !preview.stale && preview.data.timeDependent)
		|| (hasRule && stored.timeDependent && sameRule(stored.rule, draft))
		|| draft.related.some(clause => DATE_OPS.has(clause.op) || clause.window?.field)
	);
	const runningPass = stored?.pass?.running ? stored.pass : null;
	// A refusal belongs under the part it names — unless that part is not on
	// screen, when it is said at the top.
	const partShown = target => {
		if (!target || target.section === null) return false;
		if (target.section === 'card') return true;
		if (!draft.card_id) return false;
		if (target.section === 'match') return partCount(draft) > 1;
		if (target.section === 'related') return target.index < draft.related.length;
		return true;
	};
	const topRefusal = refusal && !partShown(refusal.target) ? refusal.text : null;
	const sample = Array.isArray(preview.data?.sample) ? preview.data.sample : [];

	const footer = (
		<div className="tag-rule-editor__footer">
			{hasRule ? (
				<Button danger onClick={stop} loading={stopping} disabled={saving}>Stop applying by rule…</Button>
			) : <span />}
			<Space wrap className="tag-rule-editor__footer-main">
				<Button onClick={onClose}>Cancel</Button>
				<Button type="primary" onClick={save} loading={saving} disabled={load.loading || Boolean(load.error)}>Save rule</Button>
			</Space>
		</div>
	);

	return (
		<Modal
			open={open}
			title="Applied by a rule"
			onCancel={onClose}
			width={760}
			destroyOnHidden
			className="tag-rule-editor"
			footer={footer}
		>
			<div className="modal-identity">
				<RecordIdentity kicker="Tag" badge={<TagBadge tag={tag} size="md" automatic={hasRule} />} />
			</div>

			<DialogLoadState
				loaded={!load.loading && !load.error}
				loading={load.loading}
				error={Boolean(load.error)}
				errorDetail={load.error ? tagRuleRefusal(load.error, { fallback: 'The rule could not be loaded.' }) : undefined}
				label="this rule"
				onRetry={() => setLoadAttempt(n => n + 1)}
				skeleton={<Skeleton variant="form-field" count={4} />}
				minHeight={240}
			>
				<div className="tag-rule-editor__body">
					{topRefusal ? (
						<Alert type="error" showIcon className="tag-rule-editor__refusal" message={topRefusal} data-testid="tag-rule-refusal" />
					) : null}
					<QueryRefusalPick
						inline
						refusal={elementPicks.refusal}
						disabled={saving}
						onPick={(value) => { elementPicks.pick(value); if (lastAskRef.current === 'preview') runPreview(); else save(); }}
						onRepick={() => { elementPicks.clearRefusal(); if (lastAskRef.current === 'preview') runPreview(); else save(); }}
					/>

					{hasRule ? (
						<section className="tag-rule-editor__status">
							<ToggleRow
								first
								id="tag-rule-enabled"
								label="Apply this rule"
								description={enabled
									? 'On. Turn it off to pause: the tags the rule put on come off, and tags people put on by hand stay.'
									: 'Paused. Turn it on to put the tag back wherever the rule holds.'}
								checked={enabled}
								disabled={switching}
								onChange={toggleEnabled}
							/>
							{runningPass ? <TagRulePass tagId={tagId} pass={runningPass} /> : null}
						</section>
					) : (
						<p className="meta-line tag-rule-editor__lead">
							keepr puts this tag on every item the rule holds for, and takes it off when the rule stops holding.
						</p>
					)}

					{timeDependent ? (
					<Alert
						type="info"
						showIcon
						className="tag-rule-editor__time"
						message={timeDependentNote({ daily: hasRule ? stored.daily || null : null, enabled })}
						data-testid="tag-rule-time"
					/>
				) : null}

					<section className="tag-rule-editor__section">
						<InputSelect
							elementName="tagRuleCard"
							elementLabel="Tag items of this card"
							value={draft.card_id || undefined}
							onChange={(name, value) => setCard(value || '')}
							options={cardOptions}
							placeholder="Which card’s items does it tag?"
							allowClear={false}
							status={placed('card') ? 'error' : undefined}
						/>
						{placed('card') ? <Text type="danger" className="tag-rule__error">{placed('card')}</Text> : null}
					</section>

					{draft.card_id ? (
						<>
							<section className="tag-rule-editor__section">
								<Text strong className="d-block">Only items matching</Text>
								<Text type="secondary" className="d-block tag-rule-editor__hint">
									Leave it empty to look at every {cardSingular(ruleCard)}.
								</Text>
								<ConditionBuilder
									value={draft.where}
									onChange={where => patch({ where })}
									elements={elementsFor(draft.card_id)}
									collectionId={collectionId}
									tagDisabledFor={tagDisabledFor}
								/>
								{placed('where') ? <Text type="danger" className="tag-rule__error">{placed('where')}</Text> : null}
							</section>

							<section className="tag-rule-editor__section">
								<Text strong className="d-block">Related items</Text>
								<Text type="secondary" className="d-block tag-rule-editor__hint">
									Look at the items that point at each {cardSingular(ruleCard)}: the latest one, how many, a total.
								</Text>
								{draft.related.map((clause, index) => (
									<TagRuleClause
										key={clause.key}
										clause={clause}
										index={index}
										relatedCards={relatedCards}
										cardsById={cardsById}
										elementsFor={elementsFor}
										collectionId={collectionId}
										onChange={handlerFor(clause.key)}
										onRemove={() => removeClause(clause.key)}
										error={clauseError(index)}
										phone={phone}
									/>
								))}
								{relatedCards.length ? (
									<Button
										icon={<PLUS />}
										onClick={addClause}
										disabled={draft.related.length >= RULE_LIMITS.related}
										title={draft.related.length >= RULE_LIMITS.related ? `A rule looks at ${RULE_LIMITS.related} sets of related items at most.` : undefined}
									>
										Add related items
									</Button>
								) : (
									<Text type="secondary" className="d-block tag-rule-editor__hint" data-testid="tag-rule-no-related">
										{`No card here has an item lookup pointing at ${cardSingular(ruleCard)}. Add one to a card first: that lookup is how keepr finds an item’s related items.`}
									</Text>
								)}
							</section>

							{partCount(draft) > 1 ? (
								<section className="tag-rule-editor__section">
									<Text strong className="d-block">Tag an item when</Text>
									<Segmented
										block
										vertical={phone}
										value={draft.match}
										options={[
											{ value: 'all', label: 'All of these hold' },
											{ value: 'any', label: 'Any one holds' },
										]}
										onChange={match => patch({ match })}
									/>
									{placed('match') ? <Text type="danger" className="tag-rule__error">{placed('match')}</Text> : null}
								</section>
							) : null}

							<section className="tag-rule-editor__section">
								<Text strong className="d-block">Who applies it</Text>
								<Segmented
									block
									vertical={phone}
									value={draft.strict ? 'strict' : 'shared'}
									options={[
										{ value: 'shared', label: STRICT_WORDS.shared },
										{ value: 'strict', label: STRICT_WORDS.strict },
									]}
									onChange={value => patch({ strict: value === 'strict' })}
								/>
								<Text type="secondary" className="d-block tag-rule-editor__hint">
									{draft.strict
										? 'Only the rule puts it on and takes it off. When you save, keepr takes off the tags people put on by hand, after telling you how many.'
										: 'The rule adds and removes only its own. People can still put the tag on, and take off one they put on.'}
								</Text>
								{placed('strict') ? <Text type="danger" className="tag-rule__error">{placed('strict')}</Text> : null}
							</section>

							<section className="tag-rule-editor__section tag-rule-editor__preview">
								<Button onClick={runPreview} loading={preview.loading} disabled={Boolean(gap)} title={gap || undefined}>Preview</Button>
								{preview.data ? (
									<div className={['tag-rule-preview', preview.stale ? 'tag-rule-preview--stale' : ''].filter(Boolean).join(' ')} data-testid="tag-rule-preview">
										<p className="tag-rule-preview__count">{previewWords(preview.data)}</p>
										{preview.data.applied ? (
											<p className="meta-line">{`The rule has it on ${countWords(preview.data.applied, 'item')} now.`}</p>
										) : null}
										{preview.data.removesHand ? (
											<p className="tag-rule-preview__hand">{`Saving takes it off the ${countWords(preview.data.removesHand, 'item')} people tagged by hand.`}</p>
										) : null}
										{sample.length ? (
											<>
												<p className="meta-line">For example:</p>
												<ul className="tag-rule-preview__sample" aria-label="Items it would tag">
													{sample.map(entry => {
														// Its own card's tile and its own collection (T6b).
														const sampleCollection = normalizeId(entry.collection_id);
														return (
															<li key={normalizeId(entry.item_id)}>
																<RecordBadge
																	kind="item"
																	size="xs"
																	record={cardsById[normalizeId(entry.card_id)] || null}
																	title={entry.title || 'Untitled item'}
																	to={sampleCollection ? `/collections/${sampleCollection}/items/${normalizeId(entry.item_id)}` : undefined}
																/>
															</li>
														);
													})}
												</ul>
											</>
										) : null}
										{preview.stale ? <p className="meta-line">The rule changed since this preview. Preview again to see the new count.</p> : null}
									</div>
								) : null}
							</section>
						</>
					) : null}
				</div>
			</DialogLoadState>
		</Modal>
	);
}
